import React from 'react';
import { Typography, CircularProgress, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { styled } from '@mui/material/styles';

const OverlayContainer = styled(motion.div)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10,
  padding: '32px 24px',
  background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 249, 255, 0.98) 100%)',
  textAlign: 'center',
  backdropFilter: 'blur(4px)',
});

const IconWrapper = styled(Box)({
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 24px',
  background: 'linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)',
  boxShadow: '0 8px 24px rgba(26, 35, 126, 0.15)',
  position: 'relative',
});

const SpinnerWrapper = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
});

interface LoadingOverlayProps {
  title?: string;
  subtitle?: string;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ title, subtitle }) => {
  const { t } = useTranslation();

  return (
    <OverlayContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
    >
      <IconWrapper>
        <SmartToyOutlinedIcon sx={{ fontSize: 36, color: '#1a237e' }} />
        <SpinnerWrapper>
          <CircularProgress 
            size={88} 
            thickness={2}
            sx={{ 
              color: '#1a237e',
              opacity: 0.3,
            }} 
          />
        </SpinnerWrapper>
      </IconWrapper>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: '#1a237e',
          marginBottom: '8px',
        }}
      >
        {title || t('loadingTitle')}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: '#616161',
          lineHeight: 1.6,
        }}
      >
        {subtitle || t('loadingSubtitle')}
      </Typography>
    </OverlayContainer>
  );
};
